import React from 'react';
import { useTranslation } from 'react-i18next';

import { ISelectOption } from '@private/components';
import { format } from 'date-fns/format';

import Button from 'components/Button';
import {
  DATE_MASK,
  HOUR_MSEC,
  LOCAL_SEARCH_KEY,
  MAX_MSEC,
  MINUTE_MSEC,
} from 'constants/common';
import { ICONS_MAP } from 'constants/icons';
import { appReactMemo } from 'hocs';
import { useDateLocale } from 'hooks/useDateLocale';
import {
  getDHMfromMilliseconds,
  getNormalName,
  getWDHMfromMilliseconds,
} from 'utils/common';
import { TSearch } from 'utils/types';

import { IFiltersConfig } from '../../../types';
import { FILTRATION_ICONS, ROLE_SCOPE } from '../../utils/constants';
import { getDictionaryOptionIntlKey, isFilterNumberRange } from '../../utils/helpers';
import {
  FILTER_OPERATIONS,
  IAppliedFilterValue,
  IOpenSidebarParams,
} from '../../utils/types';

import {
  ActionsContainer,
  AppliedFiltersList as FiltersList,
  ButtonWrapper,
  Container,
  FilterContent,
  FilterName,
  FilterValue,
  Operation,
  PowerButtonWrapper,
  Value,
} from './styled';

interface IAppliedFiltersList {
  className?: string;
  config: IFiltersConfig;
  filters: TSearch | undefined;
  onClickFilter?: (params: IOpenSidebarParams) => void;
  onRemoveFilter?: TChipHandler;
  onToggleFilter?: TChipHandler;
}

type TChipFilter = IAppliedFilterValue & { isAdditional?: boolean };

type TChipHandler = (
  columnKey: string,
  index?: number,
  isAdditional?: boolean,
) => (e: React.MouseEvent) => void;

const AppliedFiltersList = ({
  className,
  config,
  filters,
  onClickFilter,
  onRemoveFilter,
  onToggleFilter,
}: IAppliedFiltersList) => {
  const { i18n, t } = useTranslation();

  const locale = useDateLocale();

  const {
    betweenList,
    daysList,
    filtersSections,
    filtersSectionsMap,
    hoursList,
    ignoreList,
    intlPrefix,
    unlimitedHoursList,
    weeksList,
  } = config;

  const appliedFilersArr = React.useMemo(() => {
    if (!filters) {
      return [];
    }

    return Object.entries(filters).reduce<[string, TChipFilter][]>((prev, item) => {
      const [columnKey, filter] = item;

      if (
        !filter ||
        !item ||
        ignoreList?.includes(columnKey) ||
        columnKey === LOCAL_SEARCH_KEY
      ) {
        return prev;
      }

      const { additionalCondition, value } = filter;

      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          value.forEach((itemValue, index) => {
            if (itemValue) {
              prev.push([columnKey, { ...filter, index, value: itemValue }]);
            }
          });
        } else {
          prev.push([columnKey, filter]);
        }
      }

      if (Array.isArray(additionalCondition?.value)) {
        additionalCondition.value.forEach((itemValue, index) => {
          if (itemValue) {
            prev.push([
              columnKey,
              { ...additionalCondition, index, isAdditional: true, value: itemValue },
            ]);
          }
        });
      }

      return prev;
    }, []);
  }, [filters, ignoreList]);

  /*
    A selected option is persisted as `{ label, value }`, so its label stays frozen in whichever
    locale it was applied in. For dictionary-backed filters the label is re-derived from the raw
    value the same way the option list builds it, which keeps the chip in step with the grid
    column after a language switch. Request filters label their options from API data, so there
    is nothing to re-translate and the stored label stands.
  */
  const getOptionLabel = (columnKey: string, option: ISelectOption) => {
    const sectionName = filtersSectionsMap?.[columnKey];
    const filterSetting = sectionName
      ? filtersSections?.get(sectionName)?.get(columnKey)
      : undefined;

    if (!option.label || !filterSetting || filterSetting.isRequestFilter) {
      return option.label;
    }

    const intlKey = getDictionaryOptionIntlKey({
      type: filterSetting.type,
      value: String(option.value),
    });

    if (!intlKey) {
      return option.label;
    }

    return i18n.exists(intlKey)
      ? t(intlKey as never)
      : getNormalName(String(option.value));
  };

  const renderValue = (filter: IAppliedFilterValue, columnKey: string) => {
    const { operation, value } = filter;

    if (columnKey === ROLE_SCOPE) {
      return t('common.filters.custom');
    }

    const selectOption = value as ISelectOption;
    if (selectOption?.label || selectOption?.label === '') {
      return getOptionLabel(columnKey, selectOption);
    }

    const includeRange = betweenList?.has(columnKey);

    if (includeRange && isFilterNumberRange(value)) {
      return `${value.min} / ${value.max}`;
    }

    if (value === MAX_MSEC) {
      return t('common.filters.unlimited');
    }

    if (
      operation === FILTER_OPERATIONS.NOT_EQUALS_NULL ||
      operation === FILTER_OPERATIONS.EQUALS_NULL
    ) {
      return 'null';
    }

    const isHoursFilter = hoursList?.has(columnKey) || unlimitedHoursList?.has(columnKey);

    if (isHoursFilter && value) {
      const time = Number(value);
      const hours = Math.floor(time / HOUR_MSEC);
      const minutes = (time - hours * HOUR_MSEC) / MINUTE_MSEC;

      return `${hours}h ${minutes}min`;
    }

    const isDaysFilters = daysList?.has(columnKey);

    if (isDaysFilters && value) {
      return getDHMfromMilliseconds(value as number);
    }

    const isWeeksFilters = weeksList?.has(columnKey);

    if (isWeeksFilters && value) {
      return getWDHMfromMilliseconds(value as number);
    }

    if (isFilterNumberRange(value)) {
      return `${format(new Date(value.min), DATE_MASK, { locale })} / ${format(
        new Date(value.max),
        DATE_MASK,
        { locale },
      )}`;
    }

    return value as string;
  };

  const onClickItem =
    ({ columnKey, index, isAdditional }: IOpenSidebarParams) =>
    () => {
      onClickFilter?.({ columnKey, index, isAdditional });
    };

  return (
    <FiltersList $isVisible={!!appliedFilersArr.length} className={className}>
      {appliedFilersArr.map(([columnKey, filter]) => {
        const columnFilter = filters?.[columnKey];
        const conditionValue = filter.isAdditional
          ? columnFilter?.additionalCondition?.value
          : columnFilter?.value;
        const isPerValueChip =
          typeof filter.index === 'number' && Array.isArray(conditionValue);
        const isChipDisabled =
          filter.enabled === false ||
          (isPerValueChip && !!filter.disabledIndexes?.includes(filter.index as number));

        return (
          <Container
            $isDisabled={isChipDisabled}
            key={`${columnKey}-${filter.operation}-${(filter.value as ISelectOption)?.value || filter.value}`}
            onClick={onClickItem({
              columnKey,
              index: filter.index,
              isAdditional: filter.isAdditional,
            })}
          >
            <FilterContent>
              <FilterName size='xs' variant='regular'>
                {t(`${intlPrefix}.${columnKey}` as never)}
              </FilterName>
              <Value>
                {filter.operation && (
                  <Operation>{FILTRATION_ICONS[filter.operation]}</Operation>
                )}
                <FilterValue size='sm'>{renderValue(filter, columnKey)}</FilterValue>
              </Value>
            </FilterContent>
            <ActionsContainer>
              {onToggleFilter && (
                <PowerButtonWrapper>
                  <Button
                    iconSize={12}
                    onClick={onToggleFilter(
                      columnKey,
                      isPerValueChip ? filter.index : undefined,
                      filter.isAdditional,
                    )}
                    size='xxs'
                    startIcon={<ICONS_MAP.Power />}
                    testIds={{ container: `${columnKey}TogglePowerButton` }}
                    variant='icon'
                  />
                </PowerButtonWrapper>
              )}
              {onRemoveFilter && (
                <ButtonWrapper>
                  <Button
                    iconSize={9}
                    onClick={onRemoveFilter(columnKey, filter.index, filter.isAdditional)}
                    size='xxs'
                    startIcon={<ICONS_MAP.Close />}
                    variant='icon'
                  />
                </ButtonWrapper>
              )}
            </ActionsContainer>
          </Container>
        );
      })}
    </FiltersList>
  );
};

export default appReactMemo(AppliedFiltersList);
